import { useState } from 'react';
import type { Request, SCData, TabState, Touchpoint } from '../../types';
import { buildSCList, buildTouchpointSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueByOpp, validReqAges, isReqAged, ageSeverity, formatReqAge, splitUserSCNames } from '../../utils/opportunities';
import { useRequests } from '../../context/RequestsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { getRequestURL } from '../../utils/opportunities';
interface Props {
  state: TabState;
}
type RequestViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-743" data-mp="3m00m3bunred3">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-744" data-mp="3h04ihdpvhoy2" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-745" data-mp="3vrehb7fkts0f" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-746" data-mp="3vsxxyz55a52q" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-747" data-mp="3qt37gwpa7hi7" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-748" data-mp="3338u8e11vqxf">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-749" data-mp="2y4o271vycdk2" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-750" data-mp="3c2vugwi1mqow" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-751" data-mp="3c4p5enzovx2p" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-752" data-mp="381si4jz7fcsf" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-753" data-mp="rcwrl1fuv7t5">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-754" data-mp="nc32i4qofmyo" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-755" data-mp="ndnylysn3dyn" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-756" data-mp="3g4qnxz3uvtqj" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-757" data-mp="3l4wflfa4lg8m" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-758" data-mp="3l3bndwbaa9dx" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-759" data-mp="3q3g4nok0n857" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-760" data-mp="3q4radtx0upyy" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-761" data-mp="3u5uoaaviebxj" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-762" data-mp="3u38f66syzod7" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-763" data-mp="32eesprbz5ev6">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-764" data-mp="36fqhjun0xw2b" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-765" data-mp="36e5ok9gcyl6k" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-766" data-mp="3b7qo45xlnn7v" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-767" data-mp="2eo15ce4c446x">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-768" data-mp="2iobc3szyaymu" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-769" data-mp="2b7541fy1sfht" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-770" data-mp="2b63qn4tk7gwx" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-771" data-mp="267ad5ce9vpwr" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-772" data-mp="268bo7gvagceh" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-773" data-mp="224vx101zn496" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-774" data-mp="1x62id4ayt77f" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-775" data-mp="1s92kvhs6lcrx" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: RequestViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── Shared request-name link with icon ───────────────────────────────── */

const LINK_STYLE: React.CSSProperties = {
  color: '#1d4ed8',
  textDecoration: 'none'
};
function RequestLink({
  r,
  maxLen = 35
}: {
  r: Request;
  maxLen?: number;
}) {
  return <a href={getRequestURL(r)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} title={r.req_name} data-id="element-776" data-mp="2uuwauc125dw3">
      <svg viewBox="0 0 12 12" fill="none" stroke="#1d4ed8" strokeWidth="1.4" strokeLinecap="round" style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      marginRight: 3,
      verticalAlign: 'middle',
      position: 'relative',
      top: -1
    }} data-id="element-777" data-mp="2ztfx6djkcroz">
        <path d="M5 1.5H3.5A2 2 0 0 0 1.5 3.5v5A2 2 0 0 0 3.5 10.5h5a2 2 0 0 0 2-2V8.5" data-id="element-778" data-mp="2pxcvf8smiv2k" />
        <path d="M7.5 1.5h3v3" data-id="element-779" data-mp="2bxldiwyzkuja" />
        <path d="M5.5 6.5 10.5 1.5" data-id="element-780" data-mp="2bydbufjyx6d9" />
      </svg>
      {r.req_name.substring(0, maxLen)}
    </a>;
}

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: RequestViewMode;
  onChange: (m: RequestViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-781" data-mp="1zlsrqa3j46r0">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-782" data-mp="1zm2l87ns8w1g">
          <opt.icon data-id="element-783" data-mp="29d5jl1pbuwx1" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 7) return '0–7 days';
  if (age <= 14) return '8–14 days';
  if (age < 30) return '15–29 days';
  return '30+ days';
}
const AGE_BUCKETS = ['0–7 days', '8–14 days', '15–29 days', '30+ days'];
function groupByAgeBucket(requests: Request[]): Record<string, Request[]> {
  const groups: Record<string, Request[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const r of requests) {
    const b = ageBucket(r.req_age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(r);
  }
  return groups;
}

/* ─── Display-only age for in-progress deals ──────────────────────────── */

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  requests
}: {
  requests: Request[];
}) {
  return <table className="deal-table" data-id="element-784" data-mp="1rkv3k61s502e">
      <thead data-id="element-785" data-mp="1wl9atlvk45zd">
        <tr data-id="element-786" data-mp="1wlsm4jqpbw4f">
          <th style={{
          width: '9%'
        }} data-id="element-787" data-mp="1wlj2rf05wn7p">ID</th>
          <th style={{
          width: '16%'
        }} data-id="element-788" data-mp="1wj6eyl7q3imq">Account</th>
          <th style={{
          width: '18%'
        }} data-id="element-789" data-mp="21l5hjsvn2bim">Request</th>
          <th style={{
          width: '8%'
        }} data-id="element-790" data-mp="21i9hnz08dcrj">Age</th>
          <th style={{
          width: '8%'
        }} data-id="element-791" data-mp="25l5v7bawyiom">Date</th>
          <th style={{
          width: '9%'
        }} data-id="element-792" data-mp="25jur1adk38wz">GNARR</th>
          <th style={{
          width: '9%'
        }} data-id="element-793" data-mp="2ace4m7c8u41s">Close Q</th>
          <th style={{
          width: '13%'
        }} data-id="element-794" data-mp="2aeqvxb3w5qri">Offering</th>
          <th style={{
          width: '10%'
        }} data-id="element-795" data-mp="2fc0k6nfjnwdr">Sub Region</th>
        </tr>
      </thead>
      <tbody data-id="element-796" data-mp="2kb3q0a3cm6ms">
        {requests.map(r => <tr key={r.req_id} data-id="element-797" data-mp="2kceukc4mbiey">
            <td style={{
          color: '#8892a0',
          fontSize: 9
        }} data-id="element-798" data-mp="2k90q8yce8z4v">{r.opp_id}</td>
            <td title={r.acc_name} data-id="element-799" data-mp="2pa6jqydvoqfg">{r.acc_name.substring(0, 24)}</td>
            <td title={r.req_name} data-id="element-800" data-mp="dnmk76msyq39">
              <RequestLink r={r} maxLen={30} data-id="element-801" data-mp="dnw8mps78p6u" />
            </td>
            <td className={ageSeverity(r.req_age) === 'high' ? 'c-red' : ageSeverity(r.req_age) === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: ageSeverity(r.req_age) === 'high' ? 600 : 400
        }} data-id="element-802" data-mp="8mgp525o6qqk">
              {formatReqAge(r.req_age)}
            </td>
            <td style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-803" data-mp="3ux2u54xvcsqc">{r.req_entry}</td>
            <td data-id="element-804" data-mp="wcb01e6fi586">{fmtG(r.opp_gnarr_grand_total)}</td>
            <td data-id="element-805" data-mp="rdgbdvls5mg5">{r.opp_close_q}</td>
            <td data-id="element-806" data-mp="rehvepkrxja0"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-807" data-mp="rehtul54lb7w">{r.req_offering?.substring(0, 18)}</span></td>
            <td data-id="element-808" data-mp="ml6nsm850yh1"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-809" data-mp="ml6m8hshoqex">{r.req_sub_region}</span></td>
          </tr>)}
      </tbody>
    </table>;
}
function TileView({
  requests
}: {
  requests: Request[];
}) {
  return <div className="deal-tile-grid" data-id="element-810" data-mp="1ad3yub3rjmh3">
      {requests.map(r => <div key={r.req_id} className="deal-tile" title={`${r.acc_name}\n${r.req_name}\nAge: ${formatReqAge(r.req_age)}\nGNARR: ${fmtG(r.opp_gnarr_grand_total)}\nDate: ${r.req_entry}`} data-id="element-811" data-mp="1abj0sbi8pleh">
          <div className="dt-id" data-id="element-812" data-mp="nhk9s7mofs95">{r.opp_id}</div>
          <div className="dt-acct" data-id="element-813" data-mp="9ouok5ez2rd3">{r.acc_name.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-814" data-mp="9mhyt62z3cph">
            <RequestLink r={r} maxLen={16} data-id="element-815" data-mp="eo79gkma9od1" />
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-816" data-mp="12cqi5198w35n">{r.req_entry}</div>
          <span className={`dt-age ${ageSeverity(r.req_age) === 'high' ? 'b-red' : ageSeverity(r.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-817" data-mp="16elia2ffwahu">
            {formatReqAge(r.req_age)}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  requests
}: {
  requests: Request[];
}) {
  return <div className="deal-gallery" data-id="element-818" data-mp="3d4ryqed4vzbf">
      {requests.map(r => <div key={r.req_id} className="deal-gallery-card" data-id="element-819" data-mp="3d7nxu60pwu1g">
          <div className="dg-id" data-id="element-820" data-mp="3i57c35xf16te">{r.opp_id}</div>
          <div className="dg-title" data-id="element-821" data-mp="3akvcl6zev9ph">{r.acc_name}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-822" data-mp="3ajk6h0ihtpv7">
            <RequestLink r={r} maxLen={40} data-id="element-823" data-mp="35m26hzmgekzc" />
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-824" data-mp="35lin0eh5noj7">{r.req_entry}</div>
          <div className="dg-meta" data-id="element-825" data-mp="3jks5jksu2uqc">
            <span className={`badge ${ageSeverity(r.req_age) === 'high' ? 'b-red' : ageSeverity(r.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-826" data-mp="3emqbk4tvdb03">{formatReqAge(r.req_age)}</span>
            <span className="badge b-blue" data-id="element-827" data-mp="2qut8p38edtb4">{fmtG(r.opp_gnarr_grand_total)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-828" data-mp="2mx5ds06pg0j8">{r.opp_close_q}</span>
            {isReqAged(r) && <span className="badge b-red" data-id="element-829" data-mp="30osb83cdvf3l">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-830" data-mp="2vv7ai3jeo70p">
            {r.req_sub_region} · {r.req_offering?.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  requests
}: {
  requests: Request[];
}) {
  const groups = groupByAgeBucket(requests);
  return <div className="deal-waterfall" data-id="element-831" data-mp="9mphv45zl6uz">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-832" data-mp="4o5tyy7u1kzz">
            <div className="deal-waterfall-lane-header" data-id="element-833" data-mp="3vsw5jj5dxt0b">
              <span data-id="element-834" data-mp="3vvif1obu6il6">{bucket}</span>
              <span className="lane-count" data-id="element-835" data-mp="3rsu337rtsvjz">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-836" data-mp="3msplfef6lus6">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-837" data-mp="3i2iz5l856md3">none</div>}
              {items.map(d => <div key={d.req_id} className="deal-waterfall-card" data-id="element-838" data-mp="3d0tjfs9z5kio">
                  <div className="dw-id" data-id="element-839" data-mp="385o2z8zccy1z">{d.opp_id}</div>
                  <div className="dw-acct" title={d.acc_name} data-id="element-840" data-mp="342zpgnzon2yo">{d.acc_name.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-841" data-mp="2z2va51aigea1">
                    <RequestLink r={d} maxLen={20} data-id="element-842" data-mp="1elclshasghxk" />
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-843" data-mp="1engpay4rzexb">{d.req_entry}</div>
                  <div className="dw-tags" data-id="element-844" data-mp="1jmjvilwhrr6v">
                    <span className={`badge ${ageSeverity(d.req_age) === 'high' ? 'b-red' : ageSeverity(d.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-845" data-mp="1nliza36hlhwq">{formatReqAge(d.req_age)}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-846" data-mp="vvnxh71fzzr6">{fmtG(d.opp_gnarr_grand_total)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  requests,
  mode
}: {
  requests: Request[];
  mode: RequestViewMode;
}) {
  const [dSearchId, setDSearchId] = useState('');
  const [dSearchAccount, setDSearchAccount] = useState('');
  const [dSearchRequest, setDSearchRequest] = useState('');
  const [dAgeMin, setDAgeMin] = useState('');
  const [dAgeMax, setDAgeMax] = useState('');
  const [dEntryFrom, setDEntryFrom] = useState('');
  const [dEntryTo, setDEntryTo] = useState('');
  const [dGnarrMin, setDGnarrMin] = useState('');
  const [dGnarrMax, setDGnarrMax] = useState('');
  const [dCloseQFilter, setDCloseQFilter] = useState<string[]>([]);
  const [dCloseQDropdownOpen, setDCloseQDropdownOpen] = useState(false);
  const [dOfferingFilter, setDOfferingFilter] = useState<string[]>([]);
  const [dOfferingDropdownOpen, setDOfferingDropdownOpen] = useState(false);
  const [dSubFilter, setDSubFilter] = useState<string[]>([]);
  const [dSubDropdownOpen, setDSubDropdownOpen] = useState(false);
  const dCloseQs = [...new Set(requests.map(r => r.opp_close_q).filter((v): v is string => Boolean(v)))].sort();
  const dOfferings = [...new Set(requests.map(r => r.req_offering).filter((v): v is string => Boolean(v)))].sort();
  const dSubs = [...new Set(requests.map(r => r.req_sub_region).filter((v): v is string => Boolean(v)))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = requests.filter(r => {
    if (dSearchId && !r.opp_id.toLowerCase().includes(dSearchId.toLowerCase())) return false;
    if (dSearchAccount && !r.acc_name.toLowerCase().includes(dSearchAccount.toLowerCase())) return false;
    if (dSearchRequest && !r.req_name.toLowerCase().includes(dSearchRequest.toLowerCase())) return false;
    if (dAgeMin || dAgeMax) {
      const age = r.req_age;
      if (age === null) return false;
      if (dAgeMin && age < Number(dAgeMin)) return false;
      if (dAgeMax && age > Number(dAgeMax)) return false;
    }
    if (dEntryFrom || dEntryTo) {
      const dt = parseEntry(r.req_entry);
      if (dt) {
        if (dEntryFrom) {
          const f = new Date(dEntryFrom);
          if (dt < f) return false;
        }
        if (dEntryTo) {
          const t = new Date(dEntryTo);
          if (dt > t) return false;
        }
      }
    }
    if (dGnarrMin && r.opp_gnarr_grand_total < Number(dGnarrMin)) return false;
    if (dGnarrMax && r.opp_gnarr_grand_total > Number(dGnarrMax)) return false;
    if (dCloseQFilter.length > 0 && !dCloseQFilter.includes(r.opp_close_q)) return false;
    if (dOfferingFilter.length > 0 && !dOfferingFilter.includes(r.req_offering)) return false;
    if (dSubFilter.length > 0 && !dSubFilter.includes(r.req_sub_region)) return false;
    return true;
  });
  if (requests.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-847" data-mp="2h6dd6xuv0zuo">No in-progress requests.</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const numStyle = {
    fontSize: 10,
    padding: '4px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 60,
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-848" data-mp="mssvwsm37mkh">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-849" data-mp="mpn3b3e922k0">
        {selected.length > 0 ? `${selected.length} selected` : `${items[0] === dCloseQs[0] ? 'Close Q' : items[0] === dOfferings[0] ? 'Offering' : 'Sub'} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-850" data-mp="2slqxr5xlsbx6">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-851" data-mp="2sn21x6uynlot">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-852" data-mp="3bk38ux82rvve">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-853" data-mp="1h592381tydbl" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.req_entry) - toComparable(a.req_entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-854" data-mp="jc9b7rdx25c0">
      <input type="text" placeholder="ID" value={dSearchId} onChange={e => setDSearchId(e.target.value)} style={inputStyle} data-id="element-855" data-mp="obcfh9m2o7ix" />
      <input type="text" placeholder="Account" value={dSearchAccount} onChange={e => setDSearchAccount(e.target.value)} style={inputStyle} data-id="element-856" data-mp="accv4e5leesk" />
      <input type="text" placeholder="Request" value={dSearchRequest} onChange={e => setDSearchRequest(e.target.value)} style={{
      ...inputStyle,
      width: 130
    }} data-id="element-857" data-mp="faxuycgxmwgh" />
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-858" data-mp="29oypwm9d2zba">
        <span data-id="element-859" data-mp="3i82y80sy8y03">Age</span>
        <input type="number" placeholder="min" value={dAgeMin} onChange={e => setDAgeMin(e.target.value)} style={numStyle} data-id="element-860" data-mp="3i8mk1slq06je" />
        <span data-id="element-861" data-mp="3dcdwbqtgj50m">–</span>
        <input type="number" placeholder="max" value={dAgeMax} onChange={e => setDAgeMax(e.target.value)} style={numStyle} data-id="element-862" data-mp="38j2z8heg62lw" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-863" data-mp="34gy5zjrwv7zs">
        <span data-id="element-864" data-mp="2zjfv3nuc2ip5">Entry</span>
        <input type="date" value={dEntryFrom} onChange={e => setDEntryFrom(e.target.value)} style={dateStyle} data-id="element-865" data-mp="2zgjyq3y4w22t" />
        <span data-id="element-866" data-mp="2ulwh8p1js7xq">–</span>
        <input type="date" value={dEntryTo} onChange={e => setDEntryTo(e.target.value)} style={dateStyle} data-id="element-867" data-mp="2pjxdhe1wb909" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-868" data-mp="2ljx07wqj93us">
        <span data-id="element-869" data-mp="ns2iyb7rtl59">GNARR</span>
        <input type="number" placeholder="min" value={dGnarrMin} onChange={e => setDGnarrMin(e.target.value)} style={numStyle} data-id="element-870" data-mp="nsavg1zzeofx" />
        <span data-id="element-871" data-mp="21p55ilrtzt5b">–</span>
        <input type="number" placeholder="max" value={dGnarrMax} onChange={e => setDGnarrMax(e.target.value)} style={numStyle} data-id="element-872" data-mp="21pnbwb87jnqy" />
      </div>
      {dCloseQs.length > 0 && renderDropdown(dCloseQs, dCloseQFilter, setDCloseQFilter, dCloseQDropdownOpen, setDCloseQDropdownOpen)}
      {dOfferings.length > 0 && renderDropdown(dOfferings, dOfferingFilter, setDOfferingFilter, dOfferingDropdownOpen, setDOfferingDropdownOpen)}
      {dSubs.length > 0 && renderDropdown(dSubs, dSubFilter, setDSubFilter, dSubDropdownOpen, setDSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-873" data-mp="1rr7ghw25dsbn">{filtered.length} of {requests.length} requests</div>
      <DealExpandedViewInner requests={sorted} mode={mode} data-id="element-874" data-mp="1wqaljgi4nyjm" />
    </>;
}
function DealExpandedViewInner({
  requests,
  mode
}: {
  requests: Request[];
  mode: RequestViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TableView requests={requests} data-id="element-875" data-mp="383wezdv0smay" />;
    case 'tile':
      return <TileView requests={requests} data-id="element-876" data-mp="39mgiv2tey6nc" />;
    case 'gallery':
      return <GalleryView requests={requests} data-id="element-877" data-mp="39jubp4afpt5n" />;
    case 'waterfall':
      return <WaterfallView requests={requests} data-id="element-878" data-mp="3ejp45c9uyqst" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-879" data-mp="3jcrp4w0kkrxe">
          {requests.map(r => <div key={r.req_id} className="deal-chip" data-id="element-880" data-mp="3oajpmefa5md7">
              <div className="dc-id" data-id="element-881" data-mp="3oa1kstejxzto">{r.opp_id}</div>
              <div className="dc-name" data-id="element-882" data-mp="2qnknvp9qdo8g">{r.acc_name.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 3
          }} data-id="element-883" data-mp="2vm5r7xka4o14"><RequestLink r={r} data-id="element-884" data-mp="2vn71w0xdv8ib" /></div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-885" data-mp="2vo8hmipcgj6d">{r.req_entry}</div>
              <div className="dc-tags" data-id="element-886" data-mp="30nbma21ewndt">
            <span className={`badge ${ageSeverity(r.req_age) === 'high' ? 'b-red' : ageSeverity(r.req_age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-887" data-mp="35k1y2d66owtf">{formatReqAge(r.req_age)}</span>
                <span className="badge b-blue" data-id="element-888" data-mp="ku2wclfqfjz9">{fmtG(r.opp_gnarr_grand_total)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-889" data-mp="kw76hl485f7z">{r.opp_close_q}</span>
                {isReqAged(r) && <span className="badge b-red" data-id="element-890" data-mp="20d26jr73r8tx">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-891" data-mp="20f56p41yyicd">
                {r.req_sub_region} · {r.req_offering?.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Request[];
}) {
  const ipData = data.filter(r => r.req_status === 'In Progress' && (region === 'Global' || r.opp_region === region) && (!sub || sub === 'All' || r.req_sub_region === sub) && matchesCQFilter(r, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const r of ipData) {
    const bkts = bucketProducts(r.req_products || '');
    for (const scName of splitUserSCNames(r.user_sc_name)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + r.opp_id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-892" data-mp="2gwpsp7rx1s2y">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-893" data-mp="2gwfzzcfhl6tk">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-894" data-mp="2pwmdgyb6q6sq">
        <tr data-id="element-895" data-mp="2px4fkbkj3f8k">
          <th data-id="element-896" data-mp="2pvta87bfpzfc">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-897" data-mp="2lw2erqayqv4h">{c}</th>)}
          <th data-id="element-898" data-mp="2lwu088aujch1">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-899" data-mp="1y6hu92z5vpt3">
        {scRows.map(row => <tr key={row.full} data-id="element-900" data-mp="1y79exir8034l">
            <td title={row.full} data-id="element-901" data-mp="1y4dftr3mz8ek">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-902" data-mp="1t9hl2j70m6xr">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-903" data-mp="26zuw4uf7e1rb">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-904" data-mp="228l6vuxkh0fy">—</span>}
              </td>)}
            <td data-id="element-905" data-mp="1eiiippwrwemm"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-906" data-mp="1eiik9ucf8moq">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-907" data-mp="1agnheleue18u">
        <tr data-id="element-908" data-mp="1af2o0z49kock">
          <td data-id="element-909" data-mp="1afcdmllecthq">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-910" data-mp="1n7pf58qp9iyu"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-911" data-mp="1n7z9t9moweav">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-912" data-mp="1s5qy7to4liab"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-913" data-mp="1s5r2w6z2m6gn">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Touchpoint expanded view ──────────────────────────────────────────── */

function TouchpointExpandedView({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: RequestViewMode;
}) {
  const [tSearchId, setTSearchId] = useState('');
  const [tSearchName, setTSearchName] = useState('');
  const [tSearchAccount, setTSearchAccount] = useState('');
  const [tSearchSc, setTSearchSc] = useState('');
  const [tSearchComments, setTSearchComments] = useState('');
  const [tTypeFilter, setTypeFilter] = useState<string[]>([]);
  const [tTypeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [tEntryFrom, setTEntryFrom] = useState('');
  const [tEntryTo, setTEntryTo] = useState('');
  const [tCloseQFilter, setTCloseQFilter] = useState<string[]>([]);
  const [tCloseQDropdownOpen, setTCloseQDropdownOpen] = useState(false);
  const [tSubFilter, setTSubFilter] = useState<string[]>([]);
  const [tSubDropdownOpen, setTSubDropdownOpen] = useState(false);
  const tTypes = [...new Set(touchpoints.map(t => t.tp_type).filter((v): v is string => Boolean(v)))].sort();
  const tCloseQs = [...new Set(touchpoints.map(t => t.opp_close_q).filter((v): v is string => Boolean(v)))].sort();
  const tSubs = [...new Set(touchpoints.map(t => t.opp_market_area).filter((v): v is string => Boolean(v)))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = touchpoints.filter(t => {
    if (tSearchId && !t.opp_id.toLowerCase().includes(tSearchId.toLowerCase())) return false;
    if (tSearchName && !t.tp_name.toLowerCase().includes(tSearchName.toLowerCase())) return false;
    if (tSearchAccount && !t.acc_name.toLowerCase().includes(tSearchAccount.toLowerCase())) return false;
    if (tSearchSc && !t.user_sc_name.toLowerCase().includes(tSearchSc.toLowerCase())) return false;
    if (tSearchComments && !(t.tp_solution_comments || '').toLowerCase().includes(tSearchComments.toLowerCase())) return false;
    if (tTypeFilter.length > 0 && !tTypeFilter.includes(t.tp_type)) return false;
    if (tEntryFrom || tEntryTo) {
      const dt = parseEntry(t.tp_entry);
      if (dt) {
        if (tEntryFrom) {
          const f = new Date(tEntryFrom);
          if (dt < f) return false;
        }
        if (tEntryTo) {
          const te = new Date(tEntryTo);
          if (dt > te) return false;
        }
      }
    }
    if (tCloseQFilter.length > 0 && !tCloseQFilter.includes(t.opp_close_q)) return false;
    if (tSubFilter.length > 0 && !tSubFilter.includes(t.opp_market_area)) return false;
    return true;
  });
  if (touchpoints.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '12px 4px',
      textAlign: 'center'
    }} data-id="element-914" data-mp="ig7h886x7b4c">No touchpoints for this SC</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (label: string, items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-915" data-mp="5s1l5cewvpon">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-916" data-mp="aoiag9xuka3b">
        {selected.length > 0 ? `${label} (${selected.length})` : `${label} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-917" data-mp="3e5s2ohq43xou">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-918" data-mp="3e6jnqym32d0v">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-919" data-mp="3swr8xm3h6a59">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-920" data-mp="30ciswfy95a5s" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.tp_entry) - toComparable(a.tp_entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-921" data-mp="tsv58d7wzboz">
      <input type="text" placeholder="ID" value={tSearchId} onChange={e => setTSearchId(e.target.value)} style={inputStyle} data-id="element-922" data-mp="psusqy4dlakk" />
      <input type="text" placeholder="Name" value={tSearchName} onChange={e => setTSearchName(e.target.value)} style={inputStyle} data-id="element-923" data-mp="kqvo7kwwg7m1" />
      <input type="text" placeholder="Account" value={tSearchAccount} onChange={e => setTSearchAccount(e.target.value)} style={inputStyle} data-id="element-924" data-mp="av2gceu4l2al" />
      <input type="text" placeholder="SC" value={tSearchSc} onChange={e => setTSearchSc(e.target.value)} style={inputStyle} data-id="element-925" data-mp="3guk4trh5xq6i" />
      <input type="text" placeholder="Comments" value={tSearchComments} onChange={e => setTSearchComments(e.target.value)} style={{
      ...inputStyle,
      width: 120
    }} data-id="element-926" data-mp="3bywbczvneav7" />
      {renderDropdown('Type', tTypes, tTypeFilter, setTypeFilter, tTypeDropdownOpen, setTypeDropdownOpen)}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-927" data-mp="2x8p4lhf06gac">
        <span data-id="element-928" data-mp="2t7vfwltqkxge">Entry</span>
        <input type="date" value={tEntryFrom} onChange={e => setTEntryFrom(e.target.value)} style={dateStyle} data-id="element-929" data-mp="2t47njq012h39" />
        <span data-id="element-930" data-mp="326yzrs0q9gvy">–</span>
        <input type="date" value={tEntryTo} onChange={e => setTEntryTo(e.target.value)} style={dateStyle} data-id="element-931" data-mp="326gvq97tpydh" />
      </div>
      {renderDropdown('Close Q', tCloseQs, tCloseQFilter, setTCloseQFilter, tCloseQDropdownOpen, setTCloseQDropdownOpen)}
      {renderDropdown('Sub', tSubs, tSubFilter, setTSubFilter, tSubDropdownOpen, setTSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-932" data-mp="trirmelkadvc">{sorted.length} of {touchpoints.length} touchpoints</div>
      <TouchpointExpandedViewInner touchpoints={sorted} mode={mode} data-id="element-933" data-mp="g0nir2b2bwrr" />
    </>;
}
function TouchpointExpandedViewInner({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: RequestViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TouchpointTableView touchpoints={touchpoints} data-id="element-934" data-mp="18mf81f3iy7ji" />;
    case 'tile':
      return <TouchpointTileView touchpoints={touchpoints} data-id="element-935" data-mp="1cp23rr3bgclz" />;
    case 'gallery':
      return <TouchpointGalleryView touchpoints={touchpoints} data-id="element-936" data-mp="ypahz46k5rxf" />;
    case 'waterfall':
      return <TouchpointWaterfallView touchpoints={touchpoints} data-id="element-937" data-mp="13odnsqud426g" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-938" data-mp="3jf9nl704rowv">
          {touchpoints.map(t => <div key={t.tp_id} className="deal-chip" title={`${t.acc_name}\n${t.tp_name}\nType: ${t.tp_type || '—'}\nEntry: ${t.tp_entry}`} data-id="element-939" data-mp="3of4hljf7cum5">
              <div style={{
            fontSize: 9,
            color: '#8892a0'
          }} data-id="element-940" data-mp="34crqzzcp6kkr">{t.opp_id}</div>
              <div style={{
            fontSize: 10,
            fontWeight: 600,
            marginTop: 2
          }} data-id="element-941" data-mp="30bg0zspwvxc1">{t.acc_name.substring(0, 16)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 2
          }} data-id="element-942" data-mp="2vjxz92g2dsq0">
                <span title={t.tp_name} data-id="element-943" data-mp="2qlwhqlyae1gn">{t.tp_name.substring(0, 20)}{t.tp_name.length > 20 ? '…' : ''}</span>
              </div>
              <div style={{
            display: 'flex',
            gap: 4,
            marginTop: 4,
            flexWrap: 'wrap'
          }} data-id="element-944" data-mp="2ln1un7tadqqq">
                {t.tp_type && <span className="badge b-blue" style={{
              fontSize: 8
            }} data-id="element-945" data-mp="2gmnmzqvlkit8">{t.tp_type}</span>}
                <span style={{
              fontSize: 8,
              color: '#667085'
            }} data-id="element-946" data-mp="2clbvtgx2rpix">{t.tp_entry}</span>
              </div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-947" data-mp="27nsjwnnunou5">{t.opp_market_area} · {t.opp_close_q}</div>
            </div>)}
        </div>;
  }
}

/* ─── Touchpoint view renderers ──────────────────────────────────────────── */

function TouchpointTableView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const tpColumns: SortableColumn<Touchpoint>[] = [{
    key: 'opp_id',
    label: 'ID',
    width: '10%',
    sortValue: t => t.opp_id,
    render: t => <span style={{
      color: '#8892a0',
      fontSize: 9
    }} data-id="element-948" data-mp="n5878xi3p9i0">{t.opp_id}</span>
  }, {
    key: 'tp_name',
    label: 'Name',
    width: '16%',
    sortValue: t => t.tp_name,
    render: t => <span title={t.tp_name} data-id="element-949" data-mp="s39nzbs20wqb">{t.tp_name.substring(0, 22)}</span>
  }, {
    key: 'acc_name',
    label: 'Account',
    width: '14%',
    sortValue: t => t.acc_name,
    render: t => <span title={t.acc_name} data-id="element-950" data-mp="3vlunhtcz4a7p">{t.acc_name.substring(0, 18)}</span>
  }, {
    key: 'user_sc_name',
    label: 'SC',
    width: '10%',
    sortValue: t => t.user_sc_name,
    render: t => <span data-id="element-951" data-mp="8ftxvpeiys2m">{t.user_sc_name}</span>
  }, {
    key: 'tp_type',
    label: 'Type',
    width: '12%',
    sortValue: t => t.tp_type || '',
    render: t => t.tp_type ? <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-952" data-mp="1ft41lvk76pbm">{t.tp_type}</span> : <span data-id="element-953" data-mp="1juh7u6r7cuj2">—</span>
  }, {
    key: 'tp_entry',
    label: 'Entry',
    width: '10%',
    sortValue: t => t.tp_entry,
    render: t => <span style={{
      fontSize: 9,
      color: '#667085'
    }} data-id="element-954" data-mp="1iev19w6v44rm">{t.tp_entry}</span>
  }, {
    key: 'tp_solution_comments',
    label: 'Comments',
    width: '18%',
    sortValue: t => t.tp_solution_comments || '',
    render: t => <span title={t.tp_solution_comments} data-id="element-955" data-mp="1sd13kewkzcx0">{t.tp_solution_comments ? t.tp_solution_comments.substring(0, 30) + (t.tp_solution_comments.length > 30 ? '…' : '') : '—'}</span>
  }];
  return <SortableTable columns={tpColumns} data={touchpoints} rowKey={t => t.tp_id} data-id="element-956" data-mp="21bw7fkc5kax5" />;
}
function TouchpointTileView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-tile-grid" data-id="element-957" data-mp="2b2hbhnali7ng">
      {touchpoints.map(t => <div key={t.tp_id} className="deal-tile" title={`${t.acc_name}\n${t.tp_name}\nType: ${t.tp_type || '—'}\nEntry: ${t.tp_entry}`} data-id="element-958" data-mp="2b4twk9bngy4q">
          <div className="dt-id" data-id="element-959" data-mp="2k2xcmq3v92p5">{t.opp_id}</div>
          <div className="dt-acct" data-id="element-960" data-mp="2k3p35milwa8k">{t.acc_name.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-961" data-mp="2g2vfmu92sxg7">
            <span title={t.tp_name} data-id="element-962" data-mp="2ypme2xhohpy6">{t.tp_name.substring(0, 16)}</span>
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-963" data-mp="33p8t9ch2hg9m">{t.tp_entry}</div>
          <span className="badge b-blue" style={{
        fontSize: 8
      }} data-id="element-964" data-mp="33muuotn6hlxb">{t.tp_type || '—'}</span>
        </div>)}
    </div>;
}
function TouchpointGalleryView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-gallery" data-id="element-965" data-mp="2fz4naygep4fg">
      {touchpoints.map(t => <div key={t.tp_id} className="deal-gallery-card" data-id="element-966" data-mp="2fxjvvhpe21lt">
          <div className="dg-id" data-id="element-967" data-mp="2krehwrsguso2">{t.opp_id}</div>
          <div className="dg-title" data-id="element-968" data-mp="2ks7gv6l6odvn">{t.acc_name}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-969" data-mp="2709fjhxprma1">
            <span title={t.tp_name} data-id="element-970" data-mp="2b0a59yq9jjwe">{t.tp_name.substring(0, 30)}{t.tp_name.length > 30 ? '…' : ''}</span>
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-971" data-mp="1x0imlkosxfc2">{t.tp_entry}</div>
          <div style={{
        fontSize: 9,
        color: '#344054',
        marginBottom: 4,
        lineHeight: 1.3
      }} data-id="element-972" data-mp="220etbrovvkvb">
            <span title={t.tp_solution_comments} data-id="element-973" data-mp="7k6ljahm9w0h">{t.tp_solution_comments ? t.tp_solution_comments.substring(0, 50) + (t.tp_solution_comments.length > 50 ? '…' : '') : '—'}</span>
          </div>
          <div className="dg-meta" data-id="element-974" data-mp="3uwh9w6kxi3zc">
            {t.tp_type && <span className="badge b-blue" data-id="element-975" data-mp="3ux8zn0ruh7hp">{t.tp_type}</span>}
            <span className="badge b-gray" data-id="element-976" data-mp="3pyfh2txpaqao">{t.opp_close_q}</span>
          </div>
          <div className="dg-footer" data-id="element-977" data-mp="qi94b7jkmwpu">
            {t.opp_market_area} · {t.user_sc_name}
          </div>
        </div>)}
    </div>;
}
const TP_TYPE_BUCKETS = ['Internal Alignment', 'Discovery', 'Live Demo', 'Customer Meeting'];
function TouchpointWaterfallView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const groups: Record<string, Touchpoint[]> = {};
  const BUCKETS_WITH_OTHERS = [...TP_TYPE_BUCKETS, 'Others'];

  // Initialize all buckets
  for (const b of BUCKETS_WITH_OTHERS) groups[b] = [];

  // Distribute touchpoints into buckets
  for (const t of touchpoints) {
    const tpType = t.tp_type || '';
    if (TP_TYPE_BUCKETS.includes(tpType)) {
      groups[tpType].push(t);
    } else {
      groups['Others'].push(t);
    }
  }
  return <div className="deal-waterfall" data-id="element-978" data-mp="2s20pxgkrvdtq">
      {BUCKETS_WITH_OTHERS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-979" data-mp="31u6fykrgrl6a">
            <div className="deal-waterfall-lane-header" data-id="element-980" data-mp="31vrc2etffc69">
              <span data-id="element-981" data-mp="35uqbjjwe2gqb">{bucket}</span>
              <span className="lane-count" data-id="element-982" data-mp="35xmdrkf9rrkk">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-983" data-mp="3asrum4tteg1s">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-984" data-mp="3fu8xg5vv0cl0">none</div>}
              {items.map(t => <div key={t.tp_id} className="deal-waterfall-card" data-id="element-985" data-mp="1lgzrup6fpb81">
                  <div className="dw-id" data-id="element-986" data-mp="1qe9gi2m01iut">{t.opp_id}</div>
                  <div className="dw-acct" title={t.acc_name} data-id="element-987" data-mp="1qgvmhxt8rqax">{t.acc_name.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-988" data-mp="3sa8j0bd1umq7">
                    <span title={t.tp_name} data-id="element-989" data-mp="3ncp6bfw02i0d">{t.tp_name.substring(0, 20)}</span>
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-990" data-mp="5w7bs6c9z66f">{t.tp_entry}</div>
                  <div className="dw-tags" data-id="element-991" data-mp="y5spleun6uy">
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-992" data-mp="39dfmm68l55rn">{t.tp_type || '—'}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<RequestViewMode>('cards');
  const [expandedViewMode, setExpandedViewMode] = useState<'deals' | 'touchpoints'>('deals');
  const [scSearch, setScSearch] = useState('');
  const {
    requests,
    touchpoints
  } = useRequests();

  // Touchpoints filters
  const [tpSearchId, setTpSearchId] = useState('');
  const [tpSearchName, setTpSearchName] = useState('');
  const [tpSearchAccount, setTpSearchAccount] = useState('');
  const [tpSearchSc, setTpSearchSc] = useState('');
  const [tpTypeFilter, setTpTypeFilter] = useState<string[]>([]);
  const [tpTypeDropdownOpen, setTpTypeDropdownOpen] = useState(false);
  const [tpEntryFrom, setTpEntryFrom] = useState('');
  const [tpEntryTo, setTpEntryTo] = useState('');
  const tpTypes = [...new Set(touchpoints.map(t => t.tp_type).filter(Boolean))].sort();
  const parseEntryDate = (entry: string): Date | null => {
    if (!entry) return null;
    const parts = entry.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yy] = parts.map(Number);
    const year = yy > 50 ? 1900 + yy : 2000 + yy;
    return new Date(year, mm - 1, dd);
  };
  const touchpointsFiltered = touchpoints.filter(t => {
    if (tpSearchId && !t.opp_id.toLowerCase().includes(tpSearchId.toLowerCase())) return false;
    if (tpSearchName && !t.tp_name.toLowerCase().includes(tpSearchName.toLowerCase())) return false;
    if (tpSearchAccount && !t.acc_name.toLowerCase().includes(tpSearchAccount.toLowerCase())) return false;
    if (tpSearchSc && !t.user_sc_name.toLowerCase().includes(tpSearchSc.toLowerCase())) return false;
    if (tpTypeFilter.length > 0 && !tpTypeFilter.includes(t.tp_type)) return false;
    if (tpEntryFrom || tpEntryTo) {
      const d = parseEntryDate(t.tp_entry);
      if (d) {
        if (tpEntryFrom) {
          const from = parseEntryDate(tpEntryFrom);
          if (from && d < from) return false;
        }
        if (tpEntryTo) {
          const to = parseEntryDate(tpEntryTo);
          if (to && d > to) return false;
        }
      }
    }
    return true;
  });
  const scDataAll = buildSCList(state.r, state.sub, state, requests);
  const tpSCDataAll = buildTouchpointSCList(state.r, state.sub, state, touchpoints);
  const tpSCMap = new Map(tpSCDataAll.map(tp => [tp.n, tp]));
  if (touchpoints.length > 0 && tpSCDataAll.length <= 2) {
    const scSamples = touchpoints.slice(0, 5).map(t => t.user_sc_name);
    console.log('[ODPanel] touchpoint sc samples:', scSamples);
    console.log('[ODPanel] unique sc values:', new Set(touchpoints.map(t => t.user_sc_name)).size);
  }
  const scFiltered = scSearch ? scDataAll.filter(s => s.n.toLowerCase().includes(scSearch.toLowerCase())) : scDataAll;
  const allIP = requests.filter(r => {
    if (r.req_status !== 'In Progress') return false;
    if (state.r !== 'Global' && r.opp_region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && r.req_sub_region !== state.sub) return false;
    if (!matchesCQFilter(r, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(r => splitUserSCNames(r.user_sc_name))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = requests.filter(r => {
    if (state.r !== 'Global' && r.opp_region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && r.req_sub_region !== state.sub) return false;
    if (!matchesCQFilter(r, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueByOpp(allForGnarr, d => d.opp_gnarr);
  const ages = validReqAges(allForGnarr);
  const maxIP = Math.max(...scFiltered.map(s => s.ip), 1);
  const maxClosed = Math.max(...scFiltered.map(s => s.closed), 1);
  const scColumns: SortableColumn<SCData>[] = [{
    key: 'n',
    label: 'SC Name',
    width: '22%',
    render: p => <span className="name-link" data-id="element-993" data-mp="1mobkxkn63o0w">
          {p.n}
          <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-994" data-mp="1rhmwu06u6uzo">▾</span>
        </span>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '14%',
    sortValue: p => p.ip,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-995" data-mp="yyhatau8gdkc">
            <div className="pf" style={{
          width: `${Math.round(p.ip / maxIP * 100)}%`
        }} data-id="element-996" data-mp="rhb2qxsbxufb" />
          </div>
          <strong data-id="element-997" data-mp="mizrcimdzbuz">{p.ip}</strong>
        </>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '14%',
    sortValue: p => p.closed,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-998" data-mp="dht8w406fqz3">
            <div className="pf" style={{
          width: `${Math.round(p.closed / maxClosed * 100)}%`,
          background: '#34d399'
        }} data-id="element-999" data-mp="djcpxwtnq61x" />
          </div>
          <strong data-id="element-1000" data-mp="3qykou3dehb0">{p.closed}</strong>
        </>
  }, {
    key: 'avgAge',
    label: 'Avg age (Closed)',
    width: '11%',
    sortValue: p => p.avgAge,
    render: p => <span style={{
      color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
      fontWeight: p.avgAge > 40 ? 600 : 400
    }} data-id="element-1001" data-mp="3pyos59nmvwmv">
          {p.avgAge}d
        </span>
  }, {
    key: 'med',
    label: 'Median age (Closed)',
    width: '11%',
    sortValue: p => p.med,
    render: p => <>{p.med}d</>
  }, {
    key: 'gnarr',
    label: 'GNARR (IP)',
    width: '14%',
    sortValue: p => p.gnarr,
    render: p => <>{fmtG(p.gnarr)}</>
  }, {
    key: 'closedGnarr',
    label: 'GNARR (Closed)',
    width: '14%',
    sortValue: p => p.closedGnarr,
    render: p => <>{fmtG(p.closedGnarr)}</>
  }, {
    key: 'tpCount',
    label: 'TP',
    width: '6%',
    sortValue: p => tpSCMap.get(p.n)?.count ?? 0,
    render: p => {
      const count = tpSCMap.get(p.n)?.count ?? 0;
      return <span style={{
        color: count > 0 ? '#3B82F6' : '#8892a0',
        fontWeight: count > 0 ? 600 : 400
      }} data-id="element-1002" data-mp="166ha3wnwwd67">{count}</span>;
    }
  }];
  return <div data-id="element-1003" data-mp="s812m3i86in0">
      {/* Metrics */}
      <div className="mrow" data-id="element-1004" data-mp="x59oae2l14t0">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-1005" data-mp="x4hsp38zj72q" />
        <MetricCard label="Total Requests / person" value={scFiltered.length ? +(allForGnarr.length / scFiltered.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-1006" data-mp="2mlis3w26xpko" />
        <MedianAgeCard ages={ages} data-id="element-1007" data-mp="31oai719vnj78" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-1008" data-mp="31nhbg0ax4tp3" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — requests / touchpoints (click row to expand)" data-id="element-1009" data-mp="2wmbchkqo0a74" />
      <div className="tw" data-id="element-1010" data-mp="3ahnl8w1no171">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-1011" data-mp="35p2szyyedw5m">
          <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
          setScSearch(e.target.value);
          setExpandedSC(null);
        }} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          width: 160,
          color: '#344054',
          background: '#fff'
        }} data-id="element-1012" data-mp="3kcxkxd4kzwhn" />
        </div>
        <SortableTable columns={scColumns} data={scFiltered} rowKey={p => p.n} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={p => {
        if (expandedSC === p.n) {
          setExpandedSC(null);
        } else {
          setExpandedSC(p.n);
          setExpandedViewMode('deals');
        }
      }} renderExpandedRow={p => expandedSC === p.n ? <tr key={`exp-${p.n}`} data-id="element-1014" data-mp="cv5m9nw6hruv">
              <td colSpan={8} style={{
          padding: 0
        }} data-id="element-1015" data-mp="cw6xbsd72ecl">
                <div className="exp-inner" data-id="element-1016" data-mp="gx0nkse4058n">
                  <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }} data-id="element-1017" data-mp="gw90y72hphui">
                    <div style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#667085',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                flex: 1
              }} data-id="element-1018" data-mp="lusky1xiwjk8">
                      {expandedViewMode === 'deals' ? <>In-progress book — {p.n} ({p.ip} requests · {fmtG(p.gnarr)} GNARR)</> : <>Touchpoints — {p.n} ({tpSCMap.get(p.n)?.count ?? 0} touchpoints)</>}
                    </div>
                    <div style={{
                display: 'flex',
                border: '1px solid #d0d5dd',
                borderRadius: 5,
                overflow: 'hidden'
              }} data-id="element-1019" data-mp="14k8sh97mlca5">
                      <button onClick={() => setExpandedViewMode('deals')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  background: expandedViewMode === 'deals' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'deals' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'deals' ? 600 : 400
                }} data-id="element-1020" data-mp="2l2iuh9en0d13">
                        Requests
                      </button>
                      <button onClick={() => setExpandedViewMode('touchpoints')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  borderLeft: '1px solid #d0d5dd',
                  cursor: 'pointer',
                  background: expandedViewMode === 'touchpoints' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'touchpoints' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'touchpoints' ? 600 : 400
                }} data-id="element-1021" data-mp="2u404ca1gbihc">
                        Touchpoints
                      </button>
                    </div>
                    <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-1022" data-mp="2q3dra4hk69ru" />
                  </div>
                  {expandedViewMode === 'deals' ? <>
                      <DealExpandedView requests={p.requests} mode={dealView} data-id="element-1023" data-mp="3itm6pokpmg2q" />
                      {p.closedRequests.length > 0 && <>
                          <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#059669',
                  margin: '12px 0 6px',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em'
                }} data-id="element-1024" data-mp="39so5tf5kiood">
                            Closed — {p.n} ({p.closed} requests)
                          </div>
                          <DealExpandedView requests={p.closedRequests} mode={dealView} data-id="element-1025" data-mp="1pxjqjasktyz8" />
                        </>}
                    </> : <TouchpointExpandedView touchpoints={tpSCMap.get(p.n)?.touchpoints ?? []} mode={dealView} data-id="element-1026" data-mp="1lyasvuuf1gye" />}
                </div>
              </td>
            </tr> : null} data-id="element-1013" data-mp="1z0w49nvs8jm3" />
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-1027" data-mp="177tvcmkwjb6s" />
      <div className="tw" data-id="element-1028" data-mp="128qocwld2uw6">
        <div className="sub" data-id="element-1029" data-mp="129jxu5bpfyi0">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-1030" data-mp="1c656d0mxnrql">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-1031" data-mp="1c5d79fu4nbvk">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={requests} data-id="element-1032" data-mp="ofsoawxz4ema" />
          </table>
        </div>
      </div>

      {/* Touchpoints */}
      <SectionHeader title="Touchpoints" data-id="element-1033" data-mp="jfn3o0q4fsdp" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap'
    }} data-id="element-1034" data-mp="3dn4gl2uckbw6">
        <input type="text" placeholder="Search ID…" value={tpSearchId} onChange={e => setTpSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 110,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1035" data-mp="38v387s0av5bf" />
        <input type="text" placeholder="Search name…" value={tpSearchName} onChange={e => setTpSearchName(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1036" data-mp="3rmvu72gybq9n" />
        <input type="text" placeholder="Search account…" value={tpSearchAccount} onChange={e => setTpSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1037" data-mp="2b61be4eqqnv3" />
        <input type="text" placeholder="Search SC…" value={tpSearchSc} onChange={e => setTpSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-1038" data-mp="vnbqsl4i745l" />
        <div style={{
        position: 'relative'
      }} data-id="element-1039" data-mp="1eh7kpum9k2wt">
          <button onClick={() => setTpTypeDropdownOpen(!tpTypeDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-1040" data-mp="1jf0o6fw6mite">
            {tpTypeFilter.length > 0 ? `Type (${tpTypeFilter.length})` : 'Type ▾'}
          </button>
          {tpTypeDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-1041" data-mp="11d3ahkelaa9c">
              <div onClick={() => setTpTypeFilter(tpTypeFilter.length === tpTypes.length ? [] : [...tpTypes])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-1042" data-mp="16bwnlbpyrz8z">
                {tpTypeFilter.length === tpTypes.length ? 'Deselect all' : 'Select all'}
              </div>
              {tpTypes.map(t => <label key={t} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-1043" data-mp="1p2xpa7igy8w8">
                  <input type="checkbox" checked={tpTypeFilter.includes(t)} onChange={() => {
              setTpTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
            }} data-id="element-1044" data-mp="2dkolprd8msfr" />
                  {t}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-1045" data-mp="1g0bqqzmr95s6">
          <span data-id="element-1046" data-mp="1kz55swhox4ug">Entry</span>
          <input type="date" value={tpEntryFrom} onChange={e => setTpEntryFrom(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-1047" data-mp="1kw97v85ueg60" />
          <span data-id="element-1048" data-mp="yxd14em0zapr">–</span>
          <input type="date" value={tpEntryTo} onChange={e => setTpEntryTo(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-1049" data-mp="yz7lnrtdx0tb" />
        </div>
      </div>
      <div className="tw" data-id="element-1050" data-mp="g7oi9jvg7kqr">
        <SortableTable columns={[{
        key: 'opp_id',
        label: 'ID',
        width: '10%',
        sortValue: (t: Touchpoint) => t.opp_id,
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-1052" data-mp="69h5zec3tqv3">{t.opp_id}</span>
      }, {
        key: 'tp_name',
        label: 'Touchpoints Name',
        width: '18%',
        sortValue: (t: Touchpoint) => t.tp_name,
        render: (t: Touchpoint) => <span title={t.tp_name} data-id="element-1053" data-mp="20n070y5cfrl5">{t.tp_name.substring(0, 24)}</span>
      }, {
        key: 'acc_name',
        label: 'Account',
        width: '14%',
        sortValue: (t: Touchpoint) => t.acc_name,
        render: (t: Touchpoint) => <span title={t.acc_name} data-id="element-1054" data-mp="2d4swwc9oc9v6">{t.acc_name.substring(0, 18)}</span>
      }, {
        key: 'user_sc_name',
        label: 'SC',
        width: '10%',
        sortValue: (t: Touchpoint) => t.user_sc_name,
        render: (t: Touchpoint) => <span title={t.user_sc_name} data-id="element-1055" data-mp="2mv5oupjtj5bc">{t.user_sc_name}</span>
      }, {
        key: 'tp_type',
        label: 'Type',
        width: '12%',
        sortValue: (t: Touchpoint) => t.tp_type || '',
        render: (t: Touchpoint) => t.tp_type ? <span className="badge b-blue" style={{
          fontSize: 8
        }} data-id="element-1056" data-mp="1u79uhrhq1phq">{t.tp_type}</span> : <span style={{
          color: '#d1d5db'
        }} data-id="element-1057" data-mp="1z5becemz1su9">—</span>
      }, {
        key: 'tp_entry',
        label: 'Entry',
        width: '12%',
        sortValue: (t: Touchpoint) => {
          const p = t.tp_entry.split('/');
          if (p.length !== 3) return 0;
          const [dd, mm, yy] = p.map(Number);
          return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd).getTime();
        },
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-1058" data-mp="2py0cy1w3mqop">{t.tp_entry}</span>
      }, {
        key: 'tp_solution_comments',
        label: 'Comments',
        width: '16%',
        sortValue: (t: Touchpoint) => t.tp_solution_comments || '',
        render: (t: Touchpoint) => <span title={t.tp_solution_comments} data-id="element-1059" data-mp="2uwbqonpillc7">{t.tp_solution_comments ? t.tp_solution_comments.substring(0, 35) + (t.tp_solution_comments.length > 35 ? '…' : '') : '—'}</span>
      }]} data={touchpointsFiltered} rowKey={(t: Touchpoint) => t.tp_id} defaultSort={{
        key: 'entry',
        dir: 'desc'
      }} data-id="element-1051" data-mp="g6dg1ohnil1r" />
      </div>
    </div>;
}